import { useQuery } from "@tanstack/react-query";
import React, { type ReactNode, useMemo } from "react";
import { ApiError } from "../api/client";
import type { BaseForecastViewNode } from "../api/models/baseForecastView";
import type { ForecastDataPointResponseApi } from "../api/models/forecast";
import type { ForecastMetricsViewNode } from "../api/models/forecastMetricsView";
import {
  getBaseForecastGraphql,
  getForecastMetricsGraphql,
} from "../api/services/baseForecastGraphqlService";
import { getForecast } from "../api/services/forecastService";
import { generateForecastData } from "../data/demo-data";
import { parseDateOnly } from "../lib/forecast-dates";
import type { ForecastDataPoint, ForecastMetrics } from "../types";
import { useApp } from "./AppContext";
import { BaseForecastContext } from "./BaseForecastContextDef";

function mapGraphqlNodeToForecastDataPoint(
  node: BaseForecastViewNode,
): ForecastDataPoint {
  return {
    date: parseDateOnly(node.date),
    openingBalance: node.openingBalance,
    balance: node.balance,
    incoming: node.incoming,
    outgoing: node.outgoing,
    scenario: "base",
    confidenceScore: node.confidenceScore ?? undefined,
  };
}

function mapApiToForecastDataPoint(
  api: ForecastDataPointResponseApi,
): ForecastDataPoint {
  return {
    date: parseDateOnly(api.date),
    openingBalance: api.openingBalance,
    balance: api.balance,
    incoming: api.incoming,
    outgoing: api.outgoing,
    scenario: "base",
    confidenceScore: api.confidenceScore,
    contributingLayers: api.contributingLayers,
  };
}

function mapGraphqlNodeToMetrics(
  node: ForecastMetricsViewNode,
): ForecastMetrics {
  return {
    minCash: node.minCash,
    minCashDate: node.minCashDate
      ? parseDateOnly(node.minCashDate)
      : new Date(),
    endingCash: node.endingCash,
    coverageMonths: node.coverageMonths,
    isNoBurn: node.isNoBurn,
    avgMonthlyInflow: node.avgMonthlyInflow,
    avgMonthlyOutflow: node.avgMonthlyOutflow,
    confidenceScore: node.confidenceScore,
    startingCash: node.startingCash,
  };
}

export const BaseForecastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { mode } = useApp();
  const isDemo = mode === "demo";

  // Demo forecast — stable reference, computed once
  const demoForecast = useMemo(() => generateForecastData("base"), []);

  // Live forecast via React Query — REST supplies the layer availability,
  // resolution, run id and readiness (404) state that the GraphQL views do not carry.
  const {
    data: forecastResponse,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["forecast", "90d"],
    queryFn: () => getForecast("90d"),
    enabled: !isDemo,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Ground-truth base forecast timeline via GraphQL (vw_BaseForecast — latest
  // completed daily run). Preferred as the timeline source in live mode; the
  // REST timeline remains the fallback until the GraphQL points resolve.
  const { data: graphqlBaseForecast } = useQuery({
    queryKey: ["baseForecastGraphql"],
    queryFn: () => getBaseForecastGraphql({ periodKey: { eq: "90d" } }, 400),
    enabled: !isDemo,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Headline metrics via GraphQL (vw_ForecastMetrics — same latest completed run
  // the REST response reads, so the values are identical).
  const { data: graphqlMetrics } = useQuery({
    queryKey: ["forecastMetricsGraphql", "90d"],
    queryFn: () => getForecastMetricsGraphql("90d"),
    enabled: !isDemo,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const value = useMemo(() => {
    const graphqlForecast =
      graphqlBaseForecast && graphqlBaseForecast.edges.length > 0
        ? graphqlBaseForecast.edges.map((e) =>
            mapGraphqlNodeToForecastDataPoint(e.node),
          )
        : null;
    const liveForecast =
      graphqlForecast ??
      (forecastResponse
        ? forecastResponse.timeline.map(mapApiToForecastDataPoint)
        : null);
    const liveMetrics = graphqlMetrics
      ? mapGraphqlNodeToMetrics(graphqlMetrics)
      : null;
    const baseForecast = isDemo ? demoForecast : (liveForecast ?? []);
    const forecastNotReady =
      !isDemo && queryError instanceof ApiError && queryError.status === 404;

    return {
      baseForecast,
      isLoading: isDemo ? false : queryLoading,
      error: isDemo
        ? null
        : queryError instanceof Error
          ? queryError.message
          : queryError
            ? "Failed to load base forecast"
            : null,
      forecastNotReady,
      metrics: isDemo ? null : liveMetrics,
      availableLayers: isDemo ? [] : (forecastResponse?.availableLayers ?? []),
      missingLayers: isDemo ? [] : (forecastResponse?.missingLayers ?? []),
      resolution: isDemo ? null : (forecastResponse?.resolution ?? null),
      forecastRunId: isDemo
        ? undefined
        : (forecastResponse?.forecastRunId ?? undefined),
      refetch: isDemo
        ? () => {}
        : () => {
            refetch();
          },
    };
  }, [
    demoForecast,
    forecastResponse,
    graphqlBaseForecast,
    graphqlMetrics,
    queryLoading,
    queryError,
    isDemo,
    refetch,
  ]);

  return (
    <BaseForecastContext.Provider value={value}>
      {children}
    </BaseForecastContext.Provider>
  );
};
